import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { daysUntilSunset } from '@/lib/sunset-dates';

export type SmokeLogRow = {
  id: string;
  provider: 'ghl' | 'meta' | 'google_ads';
  status: 'ok' | 'failed' | 'timed_out';
  latency_ms: number | null;
  error_snippet: string | null;
  api_version: string | null;
  sunset_date: string | null;
  ran_at: string;
};

export type ProviderState = {
  status: 'ok' | 'failed' | 'timed_out' | 'unknown';
  ran_at: string | null;
  api_version: string | null;
  sunset_date: string | null;
};

export type BadgeState = {
  color: 'green' | 'yellow' | 'red' | 'unknown';
  providers: { ghl: ProviderState; meta: ProviderState; google_ads: ProviderState };
  lastRunAt: string | null;
  sunsetWarnings: Array<{ provider: SmokeLogRow['provider']; api_version: string | null; sunset_date: string; days: number }>;
};

const UNKNOWN: ProviderState = { status: 'unknown', ran_at: null, api_version: null, sunset_date: null };
const STALE_THRESHOLD_MS = 36 * 60 * 60 * 1000;  // D-16
const SUNSET_WARN_DAYS = 90;                      // D-17

export function computeBadgeState(rows: SmokeLogRow[], now: Date = new Date()): BadgeState {
  // Latest row per provider
  const byProvider: Record<'ghl' | 'meta' | 'google_ads', SmokeLogRow | null> = {
    ghl: null,
    meta: null,
    google_ads: null,
  };
  for (const row of rows) {
    const existing = byProvider[row.provider];
    if (!existing || new Date(row.ran_at) > new Date(existing.ran_at)) {
      byProvider[row.provider] = row;
    }
  }

  const toState = (r: SmokeLogRow | null): ProviderState =>
    r
      ? { status: r.status, ran_at: r.ran_at, api_version: r.api_version, sunset_date: r.sunset_date }
      : { ...UNKNOWN };

  const providers = {
    ghl: toState(byProvider.ghl),
    meta: toState(byProvider.meta),
    google_ads: toState(byProvider.google_ads),
  };

  // Determine lastRunAt (max ran_at across all rows)
  const ranAts = rows.map(r => new Date(r.ran_at).getTime()).sort((a, b) => b - a);
  const lastRunMs = ranAts[0] ?? null;
  const lastRunAt = lastRunMs != null ? new Date(lastRunMs).toISOString() : null;

  // Color derivation
  let color: BadgeState['color'] = 'green';
  if (lastRunMs == null) {
    color = 'unknown';
  } else if (Object.values(providers).some(p => p.status === 'failed' || p.status === 'timed_out')) {
    color = 'red';
  } else if (now.getTime() - lastRunMs > STALE_THRESHOLD_MS) {
    color = 'yellow';
  } else if (Object.values(providers).some(p => p.status === 'unknown')) {
    // stale-equivalent: missing provider data in last run
    color = 'yellow';
  }

  // Sunset warnings
  const sunsetWarnings: BadgeState['sunsetWarnings'] = [];
  const providerKeys = ['ghl', 'meta', 'google_ads'] as const;
  for (const key of providerKeys) {
    const state = providers[key];
    const days = daysUntilSunset(state.sunset_date, now);
    if (days !== null && days <= SUNSET_WARN_DAYS) {
      sunsetWarnings.push({
        provider: key,
        api_version: state.api_version,
        sunset_date: state.sunset_date!,
        days,
      });
    }
  }

  return { color, providers, lastRunAt, sunsetWarnings };
}

const STALE_TIME_MS = 60_000;

export function useIntegrationHealth(): UseQueryResult<BadgeState, Error> {
  return useQuery<BadgeState, Error>({
    queryKey: ['integration_smoke_logs', 'last_run'],
    queryFn: async () => {
      // Fetch last 30 rows — enough to get latest per provider with margin.
      // Cast: integration_smoke_logs is added by migration 20260423000001 but the
      // generated Database types haven't been regenerated yet. Safe — the
      // CHECK + RLS policies fully constrain the row shape.
      const { data, error } = await (supabase as unknown as {
        from: (t: string) => {
          select: (cols: string) => {
            order: (col: string, opts: { ascending: boolean }) => {
              limit: (n: number) => Promise<{ data: SmokeLogRow[] | null; error: Error | null }>;
            };
          };
        };
      })
        .from('integration_smoke_logs')
        .select('id, provider, status, latency_ms, error_snippet, api_version, sunset_date, ran_at')
        .order('ran_at', { ascending: false })
        .limit(30);
      if (error) throw error;
      return computeBadgeState(data ?? []);
    },
    staleTime: STALE_TIME_MS,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}
